import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BonusHuntPage({ user }) {
  const [hunts, setHunts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHunts();
  }, []);

  const fetchHunts = async () => {
    try {
      const response = await axios.get(`${API}/hunts`);
      setHunts(response.data);
    } catch (error) {
      console.error('Failed to fetch hunts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    if (status === 'preparing') return 'text-yellow-500';
    if (status === 'active') return 'text-green-500';
    if (status === 'completed') return 'text-gray-500';
    return 'text-gray-400';
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  const huntsList = hunts || [];

  return (
    <div className="container mx-auto px-4 pt-32 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-purple-500">Bonus Hunt Tracker</h1>
          {user?.is_admin && (
            <Button onClick={() => navigate('/admin')} data-testid="admin-create-hunt">
              <Plus className="mr-2 h-4 w-4" /> Create Hunt
            </Button>
          )}
        </div>

        {huntsList.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12">
              <p className="text-center text-gray-400">No bonus hunts available yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {huntsList.map((hunt) => {
              const huntKey = hunt.hunt_id;
              const slots = hunt.slots || [];
              return (
                <Card key={huntKey} className="bg-gray-900 border-gray-800">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-2xl mb-2">
                          Hunt #{hunt.hunt_id.slice(0, 8)}
                        </CardTitle>
                        <p className="text-sm text-gray-400">
                          Status: <span className={getStatusColor(hunt.status)}>{hunt.status.toUpperCase()}</span>
                        </p>
                        <p className="text-sm text-gray-400">
                          Created: {formatDate(hunt.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Start Balance</p>
                        <p className="text-2xl font-bold text-purple-500">
                          ${hunt.start_balance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400">Opened</p>
                        <p className="text-2xl font-bold">{hunt.opened_count}</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400">Collected</p>
                        <p className="text-2xl font-bold">{hunt.collected_count}</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400">Best Win</p>
                        <p className="text-2xl font-bold text-green-500">
                          ${hunt.best_win.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400">Avg Multiplier</p>
                        <p className="text-2xl font-bold">{hunt.run_avg_x.toFixed(2)}x</p>
                      </div>
                    </div>

                    {slots.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold mb-4">Slots</h3>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Slot</TableHead>
                              <TableHead>Provider</TableHead>
                              <TableHead>Bet Size</TableHead>
                              <TableHead>Win</TableHead>
                              <TableHead>Multiplier</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {slots.map((slot) => {
                              const slotKey = slot.slot_id;
                              return (
                                <TableRow key={slotKey}>
                                  <TableCell className="font-semibold">{slot.name}</TableCell>
                                  <TableCell className="text-gray-400">{slot.provider}</TableCell>
                                  <TableCell>${slot.bet_size}</TableCell>
                                  <TableCell className="text-green-500">
                                    ${slot.win.toLocaleString()}
                                  </TableCell>
                                  <TableCell className="font-bold">{slot.multiplier.toFixed(2)}x</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}